import React, { useState } from 'react'
import PropTypes from 'prop-types'
import InputSelectOptions from 'components/molecules/input-select-options'
import { CREATABLE_PURPOSE } from 'domains/internal-request/constants'
import { Form, Formik } from 'formik'
import InputField from 'components/molecules/input-field'
import LoaderButton from 'components/molecules/loader-button'
import SelectedBorrowInventoryProductTable from './selected-borrow-inventory-product-table'
import internalRequestBorrowSchema from './internal-request-borrow-schema'
import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import { ALERT_TYPES } from 'constants/alert-constants'
import CsvUploadCard from 'components/organisms/csv-upload-card'
import InventorySelectSearchField from 'domains/internal-request/molecules/inventory-select-search-field'
import { Alert, Button } from 'reactstrap'

const INTERNAL_REQUEST_SAMPLE_CSV_ROWS = [
  'inventory_sku,quantity',
  'BKT-BG-100626-0001,1',
  'BKT-WL-100626-0002,2',
  'BKC-CH-100626-0003,1'
]
const INTERNAL_REQUEST_SAMPLE_CSV = INTERNAL_REQUEST_SAMPLE_CSV_ROWS.join('\n')
const INTERNAL_REQUEST_TEMPLATE_CSV = `\uFEFFsep=,\r\n${INTERNAL_REQUEST_SAMPLE_CSV_ROWS.join('\r\n')}\r\n`
const INTERNAL_REQUEST_SAMPLE_CSV_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(INTERNAL_REQUEST_TEMPLATE_CSV)}`

const propTypes = {
  handleSubmit             : PropTypes.func,
  selectedBorrowTableProps : PropTypes.shape({
    selectedInventories : PropTypes.arrayOf(PropTypes.object),
    onRemoveRow         : PropTypes.func
  }),
  pageUtils              : PropTypes.object,
  setSelectedInventories : PropTypes.func
}

/**
 * @param {func} handleSubmit - Handle submit form
 * @param {object} selectedBorrowTableProps - Selected Borrow Table wrapper props
 */
const InternalRequestBorrowForm = ({
  handleSubmit,
  selectedBorrowTableProps,
  setSelectedInventories,
  pageUtils
}) => {
  const [ isUploading, setIsUploading ] = useState(false)

  const handleUploadFile = (file) => {
    setIsUploading(true)
    InventoriesApiV2.indexCsv(file, { eligible_for_internal_request: true }).then((response) => {
      setSelectedInventories(response.data.inventories)
      pageUtils.setAlertMsg('CSV has been uploaded succesfully!, Please check items in table Item to be borrowed belows before Submit!', ALERT_TYPES.SUCCESS)
    }).catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsUploading(false))
  }

  return (
    <Formik
      enableReinitialize={ false }
      initialValues={
        {
          purpose                    : '',
          requested_date             : '',
          returned_date              : '',
          preorder_date              : '',
          notes                      : '',
          selected_inventories       : [],
          selected_inventory         : '',
          selected_inventory_quantity: 1
        }
      }
      validationSchema={ internalRequestBorrowSchema }
      onSubmit={ handleSubmit }
    >
      {
        (formikBag) => {
          const selectedInventories = selectedBorrowTableProps?.selectedInventories || []
          const handleAddInventory = () => {
            const selectedInventory = formikBag.values.selected_inventory
            const quantity = parseInt(formikBag.values.selected_inventory_quantity || 1, 10)

            if (!selectedInventory || !selectedInventory.id) {
              pageUtils.setAlertMsg('Pilih inventory terlebih dahulu', ALERT_TYPES.WARNING)
              return
            }

            if (!quantity || quantity <= 0) {
              pageUtils.setAlertMsg('Borrow quantity harus lebih dari 0', ALERT_TYPES.WARNING)
              return
            }

            const storedQuantity = Number(selectedInventory.stored_quantity || selectedInventory.quantity || 0)

            if (storedQuantity <= 0) {
              pageUtils.setAlertMsg('Inventory ini tidak memiliki quantity yang bisa dipinjam', ALERT_TYPES.WARNING)
              return
            }

            if (quantity > storedQuantity) {
              pageUtils.setAlertMsg(`Borrow quantity maksimal ${storedQuantity}`, ALERT_TYPES.WARNING)
              return
            }

            const isAlreadyExist = selectedInventories.find((inventory) => inventory.id === selectedInventory.id)
            if (isAlreadyExist) {
              pageUtils.setAlertMsg('Inventory sudah ada di cart request', ALERT_TYPES.WARNING)
              return
            }

            setSelectedInventories([ ...selectedInventories, {
              id            : selectedInventory.id,
              inventory_sku : selectedInventory.inventory_sku,
              product_name  : selectedInventory.product_name,
              product_size  : selectedInventory.product_size,
              quantity      : quantity
            } ])

            pageUtils.setAlertMsg('Adding product inventory to borrow cart', ALERT_TYPES.SUCCESS)
            formikBag.setFieldValue('selected_inventory', '')
            formikBag.setFieldValue('selected_inventory_quantity', 1)
          }

          return (
            <Form>
              <Alert color='info'>
                <strong>{ 'Internal Request Phase 2' }</strong>
                <div>{ 'Flow create sekarang dibuat lebih ringkas: pilih purpose, isi notes/keterangan, cari inventory, lalu klik Add Item to Borrow.' }</div>
                <div>{ 'Internal Request di phase ini bersifat location-only. Produk tetap bisa dibeli customer atau dipakai Admin Deal Creation sampai ada flow komersial lain yang jalan.' }</div>
                <div>{ 'SKU PO / placeholder PO tidak ditampilkan di pencarian inventory maupun hasil CSV upload.' }</div>
              </Alert>

              <InputField
                type='select'
                name='purpose'
                label='Purpose'
                required={ true }
              >
                <InputSelectOptions options={ CREATABLE_PURPOSE } keyPrefix={ 'purpose' } />
              </InputField>

              <InputField
                type='textarea'
                name='notes'
                label='Notes / Description'
                placeholder='Keterangan kebutuhan peminjaman, nama influencer untuk Photoshoot, event, atau notes untuk tim ops'
              />

              <InputField
                type='date'
                name='requested_date'
                label='Requested Date'
                required={ true }
              />

              <InputField
                type='date'
                name='returned_date'
                label='Returned Date'
                required={ true }
              />

              <div className='mt-4 mb-3'>
                <div className='d-flex align-items-center justify-content-between mb-2'>
                  <h3 className='mb-0'>{ 'CSV Upload Format' }</h3>
                  <a
                    className='btn btn-outline-primary'
                    href={ INTERNAL_REQUEST_SAMPLE_CSV_URL }
                    download='internal-request-bulk-borrow-template.csv'
                  >
                    { 'Download CSV Template' }
                  </a>
                </div>

                <p className='text-muted mb-2'>
                  { 'HINT: header wajib ' }
                  <code>{ 'inventory_sku' }</code>
                  { ' dan optional ' }
                  <code>{ 'quantity' }</code>
                  { '. ' }
                  { 'Inventory SKU boleh memakai inventory_sku atau product SKU yang ada di Inventory List. Item belum Live tetap bisa dimasukkan selama IQ tersedia / product ada di store. Quantity kosong akan otomatis dianggap 1.' }
                  <br />
                  { 'Template download sudah diberi Excel separator hint supaya kolom rapi saat dibuka langsung di MS Excel.' }
                </p>

                <div className='table-responsive'>
                  <table className='table table-bordered table-sm mb-2'>
                    <thead>
                      <tr>
                        <th>{ 'Header / Column' }</th>
                        <th>{ 'Required?' }</th>
                        <th>{ 'Value Example' }</th>
                        <th>{ 'Notes' }</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>{ 'inventory_sku' }</code></td>
                        <td>{ 'Yes' }</td>
                        <td><code>{ 'BKT-BG-100626-0001' }</code></td>
                        <td>{ 'Inventory SKU / product SKU persis dari Inventory List. SKU PO / placeholder PO tidak akan dimasukkan. Item belum Live tetap bisa dipakai selama IQ tersedia / product ada di store.' }</td>
                      </tr>
                      <tr>
                        <td><code>{ 'quantity' }</code></td>
                        <td>{ 'No' }</td>
                        <td><code>{ '1' }</code></td>
                        <td>{ 'Angka bulat lebih dari 0. Kalau kosong / tidak valid, sistem pakai 1. Maksimal sesuai IQ / quantity di store.' }</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <pre className='bg-light border rounded p-3 mb-0'>
                  { INTERNAL_REQUEST_SAMPLE_CSV }
                </pre>
              </div>

              <CsvUploadCard
                label='Bulk Add Item To Borrow with Csv'
                onFileUpload={ handleUploadFile }
                isUploading={ isUploading }
              />

              <div className='mt-4'>
                <h3>{ 'Option 1 - Add Item Manually' }</h3>
                <p className='text-muted'>{ 'Cari inventory seperti di Create Deal, lalu klik Add Item to Borrow.' }</p>

                <InventorySelectSearchField
                  name='selected_inventory'
                  label='Cari Inventory'
                  required={ false }
                />

                <InputField
                  type='number'
                  name='selected_inventory_quantity'
                  label='Borrow Quantity'
                  min={ 1 }
                  required={ false }
                />

                <Button
                  color='primary'
                  type='button'
                  className='mb-4'
                  onClick={ handleAddInventory }
                >
                  { 'Add Item to Borrow' }
                </Button>
              </div>

              <div className='mt-5'>
                <h3>Item To Be Borrowed</h3>
                <SelectedBorrowInventoryProductTable
                  { ...selectedBorrowTableProps }
                  formikBag={ formikBag }
                />
              </div>

              <LoaderButton
                color='primary'
                type='submit'
                onSubmit={ formikBag.submitForm }
                disabled={ formikBag.isSubmitting }
                isLoading={ formikBag.isSubmitting }
                text='Borrow Product'
              />

            </Form>
          )
        }
      }
    </Formik>
  )
}

InternalRequestBorrowForm.propTypes = propTypes

export default InternalRequestBorrowForm
